import React, { useEffect, useState, memo } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Typography,
  TextField,
  CircularProgress,
} from '@mui/material';
import { FixedSizeGrid } from 'react-window';
import * as Icons from '@mui/icons-material';

interface IconSelectProps {
  value: string;
  onChange: (value: string) => void;
}

// Мемоизированный компонент иконки
const IconItem = memo(({ icon, name, isSelected, onSelect }: {
  icon: any;
  name: string;
  isSelected: boolean;
  onSelect: (name: string) => void;
}) => (
  <IconButton
    onClick={() => onSelect(name)}
    color={isSelected ? 'primary' : 'default'}
    sx={{
      bgcolor: isSelected ? 'action.selected' : 'transparent',
      '&:hover': {
        bgcolor: isSelected ? 'action.selected' : 'action.hover',
      },
    }}
  >
    {React.createElement(icon)}
  </IconButton>
));

const IconSelect: React.FC<IconSelectProps> = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [availableIcons, setAvailableIcons] = useState<[string, any][]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Получаем все иконки из @mui/icons-material
    const iconStyles = ['Filled', 'Outlined', 'Rounded', 'TwoTone', 'Sharp'];
    const icons = Object.entries(Icons)
      .filter(([key]) => !iconStyles.some(style => key.endsWith(style)))
      .map(([key, value]) => {
        // Преобразуем имя из PascalCase в camelCase
        const iconName = key.charAt(0).toLowerCase() + key.slice(1);
        return [iconName, value] as [string, any];
      });

    setAvailableIcons(icons);
    setLoading(false);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSearchQuery('');
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleSelect = (iconName: string) => {
    onChange(iconName);
    handleClose();
  };

  const filteredIcons = availableIcons.filter(([name]) =>
    name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const SelectedIcon = (Icons as any)[value.charAt(0).toUpperCase() + value.slice(1)] || Icons.Code;

  // Настройки сетки
  const COLUMN_COUNT = 6;
  const ITEM_SIZE = 48;
  const ROW_HEIGHT = ITEM_SIZE;
  const GRID_WIDTH = COLUMN_COUNT * ITEM_SIZE;
  const ROW_COUNT = Math.ceil(filteredIcons.length / COLUMN_COUNT);

  // Рендер ячейки сетки
  const Cell = ({ columnIndex, rowIndex, style }: any) => {
    const index = rowIndex * COLUMN_COUNT + columnIndex;
    if (index >= filteredIcons.length) return null;

    const [name, Icon] = filteredIcons[index];
    return (
      <Box style={style}>
        <IconItem
          icon={Icon}
          name={name}
          isSelected={value === name}
          onSelect={handleSelect}
        />
      </Box>
    );
  };

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <SelectedIcon />
      </IconButton>
      <Typography variant="caption" display="block">
        Выбранная иконка: {value || 'code'}
      </Typography>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, width: GRID_WIDTH + 32 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Выберите иконку {loading ? '' : `(${filteredIcons.length})`}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Поиск иконок..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
          />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ height: 400 }}>
              <FixedSizeGrid
                columnCount={COLUMN_COUNT}
                columnWidth={ITEM_SIZE}
                height={400}
                rowCount={ROW_COUNT}
                rowHeight={ROW_HEIGHT}
                width={GRID_WIDTH}
              >
                {Cell}
              </FixedSizeGrid>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default IconSelect; 